import { Controller, useFormContext } from 'react-hook-form';
import { FORM_FIELDS, FORM_STEPS, LABELS, PAGE } from '../../../../../constants';
import { amountTransform, PhoneInput as CommonPhoneInput, SelectInput, TextInput } from '../../../../landing.common';
import React, { FC, useCallback, useContext, useMemo } from 'react';
import { useFormStore } from '../../../../../store/selectors/formStore';
import { useDictionary } from '../../../../../store/reducers/dictionaries';
import { SourceItem } from '../../../../landing.vitrina.product/components/form.params.step/helpers';
import { anyOptionValue, FormAutoSuggest } from '../../../ui/suggest/auto';
import { FormLocationSuggest } from '../../../ui/suggest/location';
import { extendFbPayload, extendFormData } from '../../../../../store/reducers/formStore';
import { useDispatch } from 'react-redux';
import { useSelection } from '../../../../../store/reducers/selection';
import { useIsMobile } from '@sravni/react-utils';
import { useAutoFieldsManager } from './helpers';
import { AuthSmsContext } from '../../../creator/auth.context';
import { FocusedFieldProps } from '../../types';
import { RadioField } from '../../components/form/components/focused.sheet';
import { getRegionId } from '../../../../../helpers';

export const PhoneField: FC<FocusedFieldProps> = (props) => {
    const { page } = useSelection();
    const { oncePassedSteps = [], userId } = useFormStore();
    const isMobile = useIsMobile();
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    const isPhoneDisabled =
        Boolean(userId) ||
        [PAGE.CALLCENTER, PAGE.AVITO, PAGE.CALLCENTER_MAIN_FORM_EDIT].includes(page) ||
        (page == PAGE.AGENTS && oncePassedSteps.includes(FORM_STEPS.PARAMS)) ||
        props.disabled;

    const isPhoneMasked = [PAGE.CALLCENTER, PAGE.CALLCENTER_MAIN_FORM_EDIT].includes(page);

    const fieldType = isPhoneMasked ? 'password' : isMobile ? 'tel' : 'text';

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.PHONE}
            render={({ field }) => (
                <CommonPhoneInput
                    {...field}
                    disabled={isPhoneDisabled}
                    type={fieldType}
                    label={LABELS[FORM_FIELDS.PHONE]}
                    errorMessage={errors[FORM_FIELDS.PHONE]?.message as string}
                    {...props}
                />
            )}
        />
    );
};

export const SmsCodeField: FC<FocusedFieldProps> = (props) => {
    const isMobile = useIsMobile();
    const form = useFormContext();
    const {
        control,
        formState: { dirtyFields: _ },
    } = form;
    const { errorText, inProcess } = useContext(AuthSmsContext);

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.SMS_CODE}
            render={({ field }) => (
                <TextInput
                    {...field}
                    type={isMobile ? 'tel' : 'text'}
                    inputMode="numeric"
                    label={LABELS[FORM_FIELDS.SMS_CODE]}
                    errorMessage={errorText}
                    disabled={inProcess}
                    {...props}
                />
            )}
        />
    );
};

export const PurposeField: FC<FocusedFieldProps> = (props) => {
    const { isShowingOnSheet, ...other } = props;

    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    const { disabledFields = [] } = useFormStore();
    const purposes = useDictionary('purposes');

    const purposeOptions = useMemo(() => purposes.map(({ name, value }) => ({ value, label: name })), [purposes]);

    if (isShowingOnSheet) {
        return (
            <RadioField
                options={purposeOptions}
                fieldName={FORM_FIELDS.PURPOSE}
                form={form}
                disabled={disabledFields.includes(FORM_FIELDS.PURPOSE)}
            />
        );
    }

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.PURPOSE}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    label={LABELS[FORM_FIELDS.PURPOSE]}
                    disabled={disabledFields.includes(FORM_FIELDS.PURPOSE)}
                    options={purposeOptions}
                    errorMessage={errors[FORM_FIELDS.PURPOSE]?.message}
                    withCrossIcon={false}
                    {...other}
                />
            )}
        />
    );
};

export const AmountField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.AMOUNT}
            render={({ field }) => (
                <TextInput
                    {...field}
                    label={LABELS[FORM_FIELDS.AMOUNT]}
                    value={amountTransform.input(field)}
                    onChange={amountTransform.output(field)}
                    errorMessage={errors[FORM_FIELDS.AMOUNT]?.message}
                    {...props}
                />
            )}
        />
    );
};

export const RegionField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const dispatch = useDispatch();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    const changeLocation = useCallback(
        (region: Location.SuggestItem) => {
            dispatch(
                extendFormData({
                    [FORM_FIELDS.REGION_FULL_NAME]: region.fullName,
                    [FORM_FIELDS.REGION_ID]: region.id,
                    [FORM_FIELDS.REGION]: region.name,
                    [FORM_FIELDS.KLADR_ID]: region.kladr,
                    [FORM_FIELDS.REGION_LOCATION_ROUTE]: region.route,
                }),
            );
            dispatch(
                extendFbPayload({
                    regionId: getRegionId(region),
                    regionName: region.name,
                    region_latitude: region.latitude,
                    region_longitude: region.longitude,
                    region_full_name: region.fullName,
                }),
            );
        },
        [dispatch],
    );

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.REGION}
            render={({ field: { onBlur, ...field } }) => (
                <FormLocationSuggest
                    input={{
                        ...field,
                        label: LABELS[FORM_FIELDS.REGION],
                        errorMessage: errors[FORM_FIELDS.REGION]?.message,
                        ...props,
                    }}
                    onSelect={changeLocation}
                    onChange={field.onChange}
                    onBlur={onBlur}
                />
            )}
        />
    );
};

export const InitialFeeField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.INITIAL_FEE}
            render={({ field }) => (
                <TextInput
                    {...field}
                    label={LABELS[FORM_FIELDS.INITIAL_FEE]}
                    value={amountTransform.input(field)}
                    onChange={amountTransform.output(field)}
                    errorMessage={errors[FORM_FIELDS.INITIAL_FEE]?.message}
                    {...props}
                />
            )}
        />
    );
};

export const AutoBrandSuggestField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();

    const { autoBrandsOptions: options } = useAutoFieldsManager();

    const {
        control,
        formState: { errors, dirtyFields: _ },
        setValue,
        getValues,
    } = form;

    const onBrandSelect = useCallback(
        async ({ name: brandId, value: brandName }: SourceItem) => {
            const [formAutoBrandId] = getValues([FORM_FIELDS.AUTO_BRAND_ID]);
            if (formAutoBrandId === brandId) {
                return;
            }

            setValue(FORM_FIELDS.AUTO_BRAND, brandName);
            setValue(FORM_FIELDS.AUTO_BRAND_ID, brandId);
            setValue(FORM_FIELDS.AUTO_MODEL, undefined);
            setValue(FORM_FIELDS.AUTO_MODEL_ID, undefined);
            setValue(FORM_FIELDS.AUTO_YEAR, '');
        },
        [setValue, getValues],
    );

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.AUTO_BRAND}
            render={({ field: { onBlur, ...field } }) => (
                <FormAutoSuggest
                    input={{
                        ...field,
                        label: 'Марка автомобиля',
                        errorMessage: errors[FORM_FIELDS.AUTO_BRAND]?.message,
                        ...props,
                    }}
                    options={options}
                    onSelect={onBrandSelect}
                    onChange={field.onChange}
                    onBlur={onBlur}
                    emptyValue={anyOptionValue}
                />
            )}
        />
    );
};

export const AutoModelSuggestField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();

    const { autoModelsOptions: options, modelsIsLoading: loading } = useAutoFieldsManager();

    const {
        control,
        formState: { errors, dirtyFields: _ },
        setValue,
        getValues,
    } = form;

    const onModelSelect = useCallback(
        async ({ name: modelId, value: modelName }: SourceItem) => {
            const [formAutoModelId] = getValues([FORM_FIELDS.AUTO_MODEL_ID]);
            if (formAutoModelId === modelId) {
                return;
            }
            setValue(FORM_FIELDS.AUTO_MODEL, modelName);
            setValue(FORM_FIELDS.AUTO_MODEL_ID, modelId);
            setValue(FORM_FIELDS.AUTO_YEAR, '');
        },
        [getValues, setValue],
    );

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.AUTO_MODEL}
            render={({ field: { onBlur, ...field } }) => (
                <FormAutoSuggest
                    input={{
                        ...field,
                        label: 'Модель автомобиля',
                        errorMessage: errors[FORM_FIELDS.AUTO_MODEL]?.message,
                        ...props,
                    }}
                    onChange={field.onChange}
                    onBlur={onBlur}
                    onSelect={onModelSelect}
                    options={options}
                    processing={loading}
                    emptyValue={anyOptionValue}
                />
            )}
        />
    );
};

export const AutoYearSuggestField: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    const { autoYearsOptions: options, yearsIsLoading: loading } = useAutoFieldsManager();

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.AUTO_YEAR}
            render={({ field: { onBlur, ...field } }) => (
                <FormAutoSuggest
                    input={{
                        ...field,
                        label: 'Год выпуска автомобиля',
                        errorMessage: errors[FORM_FIELDS.AUTO_YEAR]?.message,
                        ...props,
                    }}
                    options={options}
                    onBlur={onBlur}
                    onChange={field.onChange}
                    processing={loading}
                />
            )}
        />
    );
};
